import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { closeModal, setModal } from '../../../../../Features/inventoryManagementSlice';
import ArrowLeft from '../../../../../Assets/Arrow left.svg';
import ChevronDown from '../../../../../Assets/ChevronDownLargeDarkMode.svg';
import Cross from '../../../../../Assets/Modals/closeIcon.svg';
import SearchIcon from '../../../../../Assets/OrderManagement/SearchIcon.svg';
import { CSVContext } from '../../../../../Context/CSVContext';
import { useOutsideClickForTwoRef } from '../../../../../Hooks/useOutsideClickForTwoRef';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { useOutsideClickHandler } from '../../../../../Hooks/useOutsideClick';
import { categoryToProductsMap, productCategories } from '../../../../../Routes/InventoryManagement/CategoryData';

function SelectCategory() {
    const dispatch = useDispatch();

    const wrapperRef = useRef(null);

    useOutsideClickHandler(wrapperRef, () => {
        handleCloseModal();
    });

    const { category, setCategory, setCreateCSVPage, productType, setProductType } = useContext(CSVContext);

    // Search states
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [productSearchTerm, setProductSearchTerm] = useState('');

    const dropdownButtonRef = useRef(null);
    const dropdownListRef = useRef(null);
    const categorySearchInputRef = useRef(null);

    const productDropdownButtonRef = useRef(null);
    const productDropdownListRef = useRef(null);
    const productSearchInputRef = useRef(null);

    useOutsideClickForTwoRef(dropdownButtonRef, dropdownListRef, () => {
        setDropdownOpen(false);
        setCategorySearchTerm(''); // Clear search when closing
    });

    useOutsideClickForTwoRef(productDropdownButtonRef, productDropdownListRef, () => {
        setProductDropdownOpen(false);
        setProductSearchTerm(''); // Clear search when closing
    });

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [productDropdownOpen, setProductDropdownOpen] = useState(false);
    const [productsList, setProductsList] = useState([]);
    const [buttonAvailable, setButtonAvailable] = useState(false);

    // Filtered lists based on search terms
    const filteredCategories = productCategories.filter((cat) =>
        cat.category.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const filteredProducts = productsList.filter((product) =>
        product.product.toLowerCase().includes(productSearchTerm.toLowerCase())
    );

    useEffect(() => {
        if (category && productType) {
            setButtonAvailable(true);
        } else {
            setButtonAvailable(false);
        }
    }, [productType, category]);

    const handleCloseModal = () => {
        dispatch(closeModal());
    };

    const handleBack = () => {
        dispatch(setModal('import-home'));
    };

    const handleNext = () => {
        if (buttonAvailable) {
            setCreateCSVPage('images');
        } else {
            if (!category) {
                showToast('Please select category.', 'warning');
            } else if (!productType) {
                showToast('Please select product type.', 'warning');
            }
            return;
        }
    };

    const handleDropdownButton = () => {
        setDropdownOpen((s) => {
            const next = !s;
            if (next) {
                setTimeout(() => {
                    categorySearchInputRef.current?.focus();
                }, 100);
            }
            return next;
        });
        setCategorySearchTerm('');
    };

    const handleProductDropdownButton = () => {
        if (!category) return; // Prevent opening if no category selected

        setProductDropdownOpen((s) => {
            const next = !s;
            if (next) {
                setTimeout(() => {
                    productSearchInputRef.current?.focus();
                }, 100);
            }
            return next;
        });
        setProductSearchTerm('');
    };

    // stopPropagation added so the parent toggle doesn't re-open the dropdown
    const handleSelectCategory = (selectedCategory, e) => {
        e?.stopPropagation();
        setCategory(selectedCategory);
        setDropdownOpen(false);
        setCategorySearchTerm('');
        // Update products for selected category
        setProductsList(categoryToProductsMap[selectedCategory] ?? []);
        setProductType('');
    };

    const handleSelectProductType = (product, e) => {
        e?.stopPropagation();
        setProductType(product);
        setProductDropdownOpen(false);
        setProductSearchTerm('');
    };

    const handleCategorySearchChange = (e) => {
        setCategorySearchTerm(e.target.value);
    };

    const handleProductSearchChange = (e) => {
        setProductSearchTerm(e.target.value);
    };

    // Prevent dropdown from closing when clicking on search input
    const handleSearchInputClick = (e) => {
        e.stopPropagation();
    };

    useEffect(() => {
        if (categoryToProductsMap.hasOwnProperty(category)) {
            setProductsList(categoryToProductsMap[category]);
            setProductType('');
        } else {
            setProductsList([]);
            setProductType('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    return (
        <div ref={wrapperRef} className="w-[44%] bg-[#FFFFFF] border border-[#EAEAEA] rounded-[9px] p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button onClick={handleBack} className="size-8 outline-none flex items-center justify-center">
                                <img src={ArrowLeft} alt="back" />
                            </button>

                            <h1 className="font-semibold text-2xl text-[#121212]">Select product type</h1>
                        </div>

                        <button onClick={handleCloseModal} className="w-11 h-11 flex items-center justify-center">
                            <img src={Cross} alt="close" />
                        </button>
                    </div>

                    <p className="font-medium text-[16px] leading-7 text-[#666666]">
                        Choose the appropriate product to access all relevant specifications and variants
                    </p>
                </div>

                {/* Category dropdown */}
                <div className="flex flex-col gap-3">
                    <h1 className="font-medium text-base text-[#121212]">Select category</h1>

                    <div
                        onClick={handleDropdownButton}
                        ref={dropdownButtonRef}
                        className={`relative cursor-pointer flex items-center justify-between px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] ${
                            dropdownOpen ? 'border-[#00c853]' : ''
                        }`}
                    >
                        <p className="font-normal w-[calc(100%-24px)] text-base text-[#666666] select-none line-clamp-1">
                            {category ? category : 'select category'}
                        </p>

                        <div className="size-6 flex items-center justify-center select-none">
                            <img src={ChevronDown} alt="toggle" />
                        </div>

                        {dropdownOpen && (
                            <div
                                ref={dropdownListRef}
                                className="bg-[#FFFFFF] max-h-56 overflow-y-auto absolute z-10 top-[58px] left-0 w-full rounded-[9px] shadow-[0px_3px_6px_0px_#0000001F]"
                            >
                                {/* Sticky search row inside same scroll container */}
                                <div
                                    className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 z-20 bg-white"
                                    onClick={handleSearchInputClick}
                                >
                                    <div className="size-6 flex items-center justify-center">
                                        <img src={SearchIcon} alt="search" />
                                    </div>

                                    <input
                                        ref={categorySearchInputRef}
                                        type="text"
                                        className="flex-1 outline-none"
                                        placeholder="Search category"
                                        value={categorySearchTerm}
                                        onChange={handleCategorySearchChange}
                                        onClick={handleSearchInputClick}
                                    />
                                </div>

                                {/* List items are direct children of the scroll container — no nested scroll */}
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map((categoryItem) => (
                                        <div
                                            key={categoryItem.id}
                                            onClick={(e) => handleSelectCategory(categoryItem.category, e)}
                                            className="px-3 py-4 font-medium text-base text-[#666666] select-none hover:bg-[#F5F5F5] cursor-pointer"
                                        >
                                            {categoryItem.category}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-3 py-4 font-medium text-base text-[#999999] select-none text-center">
                                        No categories found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Product dropdown */}
                <div className="flex flex-col gap-3">
                    <h1 className="font-medium text-base text-[#121212]">Select product type</h1>

                    <div
                        onClick={handleProductDropdownButton}
                        ref={productDropdownButtonRef}
                        className={`relative cursor-pointer flex items-center justify-between px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] ${
                            productDropdownOpen ? 'border-[#00c853]' : ''
                        } ${!category ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <p className="font-normal w-[calc(100%-24px)] text-base text-[#666666] select-none line-clamp-1">
                            {productType ? productType : 'select product type'}
                        </p>

                        <div className="size-6 flex items-center justify-center select-none">
                            <img src={ChevronDown} alt="toggle" />
                        </div>

                        {productDropdownOpen && category && (
                            <div
                                ref={productDropdownListRef}
                                className="bg-[#FFFFFF] max-h-56 overflow-y-auto absolute z-10 top-[58px] left-0 w-full rounded-[9px] shadow-[0px_3px_6px_0px_#0000001F]"
                            >
                                {/* Sticky search row inside same scroll container */}
                                <div
                                    className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 z-20 bg-white"
                                    onClick={handleSearchInputClick}
                                >
                                    <div className="size-6 flex items-center justify-center">
                                        <img src={SearchIcon} alt="search" />
                                    </div>

                                    <input
                                        ref={productSearchInputRef}
                                        type="text"
                                        className="flex-1 outline-none"
                                        placeholder="Search product type"
                                        value={productSearchTerm}
                                        onChange={handleProductSearchChange}
                                        onClick={handleSearchInputClick}
                                    />
                                </div>

                                {filteredProducts.length > 0 ? (
                                    filteredProducts.map((product) => (
                                        <div
                                            key={product.id}
                                            onClick={(e) => handleSelectProductType(product.product, e)}
                                            className="px-3 py-4 font-medium text-base text-[#666666] select-none hover:bg-[#F5F5F5] cursor-pointer"
                                        >
                                            {product.product}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-3 py-4 font-medium text-base text-[#999999] select-none text-center">
                                        No products found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <button
                onClick={handleNext}
                className={`px-4 py-2 ${buttonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg cursor-not-allowed'} text-buttonText rounded font-semibold text-base`}
            >
                Next
            </button>
        </div>
    );
}

export default SelectCategory;
